import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import AnimatedModal from "components/layout/motion/AnimatedModal";

type PostProcessPresetDialogProps = {
    open: boolean;
    busy?: boolean;
    onClose: () => void;
    onSave: (payload: { label: string; description: string }) => void;
};

const inputClass =
    "w-full rounded-r5 border border-black80 bg-black70 px-3 py-2 text-h5 text-white focus:border-primary2 focus:outline-none";

const PostProcessPresetDialog = ({
    open,
    busy = false,
    onClose,
    onSave,
}: PostProcessPresetDialogProps) => {
    const { t } = useTranslation(["settings", "common"]);
    const [label, setLabel] = useState("");
    const [description, setDescription] = useState("");

    useEffect(() => {
        if (open) {
            setLabel("");
            setDescription("");
        }
    }, [open]);

    if (!open) {
        return null;
    }

    const canSave = label.trim().length > 0 && !busy;

    return (
        <AnimatedModal visible={open} onClose={onClose} className="bg-black100">
            <div className="flex w-[min(28rem,calc(100vw-2rem))] flex-col gap-3 p-4">
                <h2 className="text-h4 font-semibold text-white">
                    {t("objectRecognition.postprocessSavePreset")}
                </h2>
                <label className="flex flex-col gap-1 text-h5 text-black40">
                    {t("objectRecognition.postprocessPresetName")}
                    <input
                        className={inputClass}
                        value={label}
                        disabled={busy}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder={t("objectRecognition.postprocessPresetNamePlaceholder")}
                    />
                </label>
                <label className="flex flex-col gap-1 text-h5 text-black40">
                    {t("objectRecognition.postprocessPresetDescription")}
                    <textarea
                        className={`${inputClass} min-h-[4.5rem] resize-y`}
                        value={description}
                        disabled={busy}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder={t("objectRecognition.postprocessPresetDescriptionPlaceholder")}
                    />
                </label>
                <p className="text-h5 text-black40">
                    {t("objectRecognition.postprocessSavePresetHint")}
                </p>
                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        disabled={busy}
                        onClick={onClose}
                        className="rounded-r5 border border-black80 bg-black80 px-4 py-2 text-h5 text-white hover:bg-black70 disabled:opacity-50"
                    >
                        {t("common:cancel")}
                    </button>
                    <button
                        type="button"
                        disabled={!canSave}
                        onClick={() => {
                            onSave({
                                label: label.trim(),
                                description: description.trim(),
                            });
                            onClose();
                        }}
                        className="rounded-r5 border border-primary2 bg-primary2 px-4 py-2 text-h5 text-white hover:bg-primary1 disabled:opacity-50"
                    >
                        {t("common:save")}
                    </button>
                </div>
            </div>
        </AnimatedModal>
    );
};

export default PostProcessPresetDialog;
